import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAstroApplications, resolveMediaUrl, updateAstroApplicationKyc } from '../../api';
import {
  PageHeader, Table, Pagination, EmptyState, TableSkeleton, StatusBadge, SearchInput,
} from '../../components/ui';
import OverviewBar from '../../components/ui/OverviewBar';
import Modal from '../../components/ui/Modal';
import { formatDateTime } from '../../utils/format';
import { ShieldCheck, Eye } from 'lucide-react';
import toast from 'react-hot-toast';

const LIMIT = 20;

/** Applications & KYC review — pending KYC docs under applications */
export default function KycReviewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('under_review');
  const [preview, setPreview] = useState<any | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-kyc-applications', page, search, status],
    queryFn: () =>
      getAstroApplications({
        page,
        limit: LIMIT,
        search: search || undefined,
        // Filter by KYC status only — do not AND with application status
        // (pending applications often have kyc.status=under_review)
        kycStatus: status || undefined,
      }).then((r) => r.data),
    placeholderData: (prev) => prev,
  });

  const kycMut = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) =>
      updateAstroApplicationKyc(id, { status: next }),
    onSuccess: (_, vars) => {
      toast.success(`KYC ${vars.next}`);
      setPreview(null);
      qc.invalidateQueries({ queryKey: ['admin-kyc-applications'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'KYC update failed'),
  });

  const rows = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="p-6">
      <PageHeader
        title="KYC Review"
        subtitle="Review identity documents on astrologer applications (pending | under_review | approved | rejected)"
      />

      <OverviewBar
        items={[
          { label: 'In queue', value: pagination?.total ?? '—', icon: <ShieldCheck size={14} /> },
        ]}
        loading={isLoading}
      />

      <div className="card p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px]">
          <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Search name, phone…" />
        </div>
        {['', 'under_review', 'approved', 'rejected'].map((s) => (
          <button
            key={s || 'all'}
            onClick={() => { setStatus(s); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium capitalize ${status === s ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300'}`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={8} cols={6} /></div>
        ) : rows.length === 0 ? (
          <EmptyState message="No applications for KYC review" icon={<ShieldCheck size={32} />} />
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="table-th">Applicant</th>
                <th className="table-th">Phone</th>
                <th className="table-th">KYC docs</th>
                <th className="table-th">Status</th>
                <th className="table-th">Submitted</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((app: any) => {
                const kyc = app.kyc || {};
                const docs = [kyc.idProofUrl, kyc.addressProofUrl, kyc.selfieUrl].filter(Boolean);
                return (
                  <tr key={app._id} className="table-row">
                    <td className="table-td font-medium">{app.name}</td>
                    <td className="table-td text-xs">{app.phone}</td>
                    <td className="table-td text-xs">{docs.length} file(s)</td>
                    <td className="table-td"><StatusBadge status={app.status} /></td>
                    <td className="table-td text-xs">{formatDateTime(app.createdAt)}</td>
                    <td className="table-td">
                      <div className="flex gap-1 justify-end">
                        <button className="btn-secondary btn-sm" onClick={() => setPreview(app)}><Eye size={12} /></button>
                        <Link to={`/astrologers/applications/${app._id}`} className="btn-primary btn-sm">Review</Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {pagination?.pages > 1 && (
          <div className="p-4 border-t">
            <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={LIMIT} onPageChange={setPage} />
          </div>
        )}
      </div>

      <Modal open={!!preview} onClose={() => setPreview(null)} title={preview ? `KYC — ${preview.name}` : 'KYC'}>
        {preview && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">ID: {preview.kyc?.idType || '—'} · {preview.kyc?.idNumber || '—'}</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'ID proof', url: preview.kyc?.idProofUrl },
                { label: 'Address proof', url: preview.kyc?.addressProofUrl },
                { label: 'Selfie', url: preview.kyc?.selfieUrl },
              ].map((d) => (
                <div key={d.label} className="bg-gray-50 rounded-xl p-2">
                  <p className="text-[11px] text-gray-500 mb-1">{d.label}</p>
                  {d.url ? (
                    <a href={resolveMediaUrl(d.url)} target="_blank" rel="noreferrer">
                      <img src={resolveMediaUrl(d.url)} alt={d.label} className="w-full h-32 object-cover rounded-lg" />
                    </a>
                  ) : (
                    <p className="text-xs text-gray-400 py-8 text-center">Missing</p>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {['under_review', 'approved', 'rejected'].map((s) => (
                <button
                  key={s}
                  className={`btn-sm capitalize ${s === 'approved' ? 'btn-primary' : s === 'rejected' ? 'btn-danger' : 'btn-secondary'}`}
                  disabled={kycMut.isPending}
                  onClick={() => kycMut.mutate({ id: preview._id, next: s })}
                >
                  Mark {s.replace('_', ' ')}
                </button>
              ))}
            </div>
            <button
              className="btn-secondary w-full"
              onClick={() => navigate(`/astrologers/applications/${preview._id}`)}
            >
              Open full application
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
